import StudentModel from '../domain/models/StudentModel';

export default class StudentService {
  constructor(repository, auditLogger) {
    this.repository = repository;
    this.auditLogger = auditLogger;
  }

  list() {
    return this.repository.getAll().map((s) => new StudentModel(s));
  }

  getById(id) {
    const raw = this.repository.getById(id);
    return raw ? new StudentModel(raw) : null;
  }

  create(data, user) {
    const student = new StudentModel(data);
    this.repository.create(student.toJSON());
    this.auditLogger?.log(student.id, 'Student created', { name: student.name }, user);
    return student;
  }

  update(id, data, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const updated = new StudentModel({ ...existing.toJSON(), ...data, id });
    this.repository.update(id, updated.toJSON());
    this.auditLogger?.log(id, 'Student updated', data, user);
    return updated;
  }

  setStatus(id, status, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const statusHistory = [
      { status, date: new Date().toISOString().slice(0, 10), by: user?.name || 'System' },
      ...(existing.statusHistory || [])
    ];
    return this.update(
      id,
      { status, statusHistory, lastUpdated: new Date().toISOString() },
      user
    );
  }

  addNote(id, content, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const note = {
      id: `note-${Math.random().toString(36).slice(2, 9)}`,
      content,
      createdAt: new Date().toISOString(),
      createdBy: user?.name || 'System'
    };
    const notes = [note, ...existing.notes.map((n) => (n.toJSON ? n.toJSON() : n))];
    const updated = this.update(id, { notes, lastUpdated: new Date().toISOString() }, user);
    this.auditLogger?.log(id, 'Note added', { noteId: note.id }, user);
    return updated;
  }

  addSupportReview(id, data, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const review = {
      id: `rev-${Math.random().toString(36).slice(2, 9)}`,
      title: data.title,
      dueDate: data.dueDate,
      assignedTo: data.assignedTo || '',
      status: data.status || 'open',
      notes: data.notes || '',
      createdBy: user?.name || 'System',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    const supportReviews = [
      review,
      ...existing.supportReviews.map((r) => (r.toJSON ? r.toJSON() : r))
    ];
    const updated = this.update(
      id,
      { supportReviews, lastUpdated: new Date().toISOString() },
      user
    );
    this.auditLogger?.log(id, 'Support review added', { reviewId: review.id }, user);
    return updated;
  }

  addIncidentReport(id, data, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const report = {
      id: `inc-${Math.random().toString(36).slice(2, 9)}`,
      title: data.title,
      description: data.description || '',
      date: data.date,
      severity: data.severity || 'medium',
      status: data.status || 'open',
      assignedTo: data.assignedTo || '',
      followUpPrompt: data.followUpPrompt || '',
      followUpDate: data.followUpDate || null,
      attachments: data.attachments || [],
      inventoryItems: data.inventoryItems || [],
      createdBy: user?.name || 'System',
      createdAt: new Date().toISOString()
    };
    const incidentReports = [
      report,
      ...existing.incidentReports.map((r) => (r.toJSON ? r.toJSON() : r))
    ];
    const updated = this.update(
      id,
      { incidentReports, lastUpdated: new Date().toISOString() },
      user
    );
    this.auditLogger?.log(id, 'Incident report added', { incidentId: report.id }, user);
    return updated;
  }

  addCertificate(id, data, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const certificate = {
      id: `cert-${Math.random().toString(36).slice(2, 9)}`,
      name: data.name,
      issueDate: data.issueDate,
      expiryDate: data.expiryDate || null,
      fileName: data.fileName || '',
      archived: false,
      archivedAt: null,
      archivedBy: null
    };
    const certificates = [
      certificate,
      ...existing.certificates.map((c) => (c.toJSON ? c.toJSON() : c))
    ];
    const updated = this.update(
      id,
      { certificates, lastUpdated: new Date().toISOString() },
      user
    );
    this.auditLogger?.log(id, 'Certificate added', { certificateId: certificate.id }, user);
    return updated;
  }

  updateCertificate(id, certificateId, updates, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const certificates = existing.certificates.map((c) => (c.toJSON ? c.toJSON() : c));
    const nextCertificates = certificates.map((cert) => {
      if (cert.id !== certificateId) return cert;
      return {
        ...cert,
        ...updates,
        updatedAt: new Date().toISOString()
      };
    });
    const updated = this.update(
      id,
      { certificates: nextCertificates, lastUpdated: new Date().toISOString() },
      user
    );
    this.auditLogger?.log(id, 'Certificate updated', { certificateId }, user);
    return updated;
  }

  archiveCertificate(id, certificateId, user) {
    return this.updateCertificate(
      id,
      certificateId,
      {
        archived: true,
        archivedAt: new Date().toISOString(),
        archivedBy: user?.name || 'System'
      },
      user
    );
  }

  restoreCertificate(id, certificateId, user) {
    return this.updateCertificate(
      id,
      certificateId,
      {
        archived: false,
        archivedAt: null,
        archivedBy: null
      },
      user
    );
  }

  deleteCertificate(id, certificateId, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const nextCertificates = existing.certificates
      .map((c) => (c.toJSON ? c.toJSON() : c))
      .filter((cert) => cert.id !== certificateId);
    const updated = this.update(
      id,
      { certificates: nextCertificates, lastUpdated: new Date().toISOString() },
      user
    );
    this.auditLogger?.log(id, 'Certificate deleted', { certificateId }, user);
    return updated;
  }

  buildMetrics(students, attendanceThreshold = 70) {
    const total = students.length;
    const active = students.filter((s) => s.status === 'Active').length;
    const inactive = students.filter((s) => s.status === 'Inactive').length;
    const underReview = students.filter((s) => s.status === 'Under Review').length;
    const highCritical = students.filter((s) => ['high', 'critical'].includes(s.riskLevel)).length;
    const activeSafeguarding = students.reduce(
      (sum, s) => sum + s.safeguardingEvents.filter((e) => e.status === 'open').length,
      0
    );
    const avgAttendance = total
      ? Math.round(students.reduce((sum, s) => sum + (s.attendance?.rate || 0), 0) / total)
      : 0;
    const belowThreshold = students.filter((s) => (s.attendance?.rate || 0) < attendanceThreshold).length;
    return {
      total,
      active,
      inactive,
      underReview,
      highCritical,
      activeSafeguarding,
      avgAttendance,
      belowThreshold
    };
  }

  groupByKeyWorker(students) {
    const groups = new Map();
    students.forEach((student) => {
      const key = student.keyWorker?.name || 'Unassigned';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(student);
    });
    return [...groups.entries()].map(([name, items]) => ({ name, students: items }));
  }

  buildCsv(students) {
    const headers = [
      'Reference',
      'Name',
      'Status',
      'Risk',
      'Attendance',
      'Key Worker',
      'Local Authority',
      'Active Safeguarding',
      'Last Updated'
    ];
    const rows = students.map((s) => [
      s.reference || s.id,
      s.name,
      s.status,
      s.riskLevel,
      s.attendance?.rate || 0,
      s.keyWorker?.name || '',
      s.localAuthority || '',
      s.safeguardingEvents.filter((e) => e.status === 'open').length,
      s.lastUpdated || ''
    ]);
    return [headers, ...rows].map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\\n');
  }
}
